import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../services/api/axios";
import { fetchProjects } from "../../services/projects";
import {
  createRole,
  createRoleAssignment,
  createRoleAssignmentsBulk,
  deleteRole,
  deleteRoleAssignment,
  fetchPermissions,
  fetchRoleAssignments,
  fetchRoles,
  updateRole,
} from "../../services/rbac";
import usePermission from "../../hooks/usePermission";
import { useUser } from "../../context/UserContext";
import Unauthorized from "../../components/Unauthorized";
import { IconButton } from "../../components/Buttons";
import { FormModal } from "../../components/form";
import CustomSelect from "../../components/CustomSelect";
import {
  Shield, Plus, Edit3, Trash2, X, Briefcase, UserCircle2, ChevronDown,
  Sparkles, ShieldCheck, Layers, Hash, Save, ChevronRight, Lock, KeyRound,
} from "lucide-react";

// Brand palette: flamingo (orange) for the primary "active management" groups
// and slate/grey shades (different intensities) for the others. Keeps the page
// visually scannable without introducing tertiary colours.
const groupTheme = {
  MANAGE_PROJECTS: {
    label: "Manage Projects",
    chip: "bg-flamingo-50 text-flamingo-700 border-flamingo-200",
    dot: "bg-flamingo-500",
  },
  MANAGE_PRESALES: {
    label: "Manage Presales",
    chip: "bg-flamingo-50/60 text-flamingo-700 border-flamingo-200",
    dot: "bg-flamingo-400",
  },
  MANAGE_SALES: {
    label: "Manage Sales",
    chip: "bg-flamingo-50/60 text-flamingo-700 border-flamingo-200",
    dot: "bg-flamingo-300",
  },
  MANAGE_CHANNEL_PARTNERS: {
    label: "Channel Partners",
    chip: "bg-slate-50 text-slate-700 border-slate-200",
    dot: "bg-slate-600",
  },
  CROSS_CUTTING: {
    label: "Cross-cutting",
    chip: "bg-gray-100 text-gray-700 border-gray-200",
    dot: "bg-gray-500",
  },
};

const predefinedRoleAccent = (code) => {
  // Flamingo gradients for the more-permissive roles, slate gradients for the
  // narrower ones — distinguishable but on-brand.
  switch (code) {
    case "ADMIN":
      return "from-flamingo-500 to-flamingo-700";
    case "SALES":
      return "from-flamingo-400 to-flamingo-600";
    case "PRESALES":
      return "from-slate-500 to-slate-700";
    case "BV_ASSOCIATE":
      return "from-slate-600 to-slate-800";
    default:
      return "from-slate-400 to-slate-600";
  }
};

const groupByPermissionGroup = (permissions) => {
  const groups = {};
  for (const p of permissions || []) {
    const g = p.permissionGroup || p.permission_group;
    if (!groups[g]) groups[g] = [];
    groups[g].push(p);
  }
  return groups;
};

const ManageRolesPage = () => {
  const { userInfo } = useUser();
  const { can, isDeveloperAdmin } = usePermission();
  const allowed = isDeveloperAdmin() || can("role.manage");

  const [activeTab, setActiveTab] = useState("roles"); // 'roles' | 'assignments'
  // Both the role editor and the assign form open as modals now. The role
  // editor's open state is derived from `editing` (null = closed, 'new' =
  // create, role obj = edit); the assign modal has its own boolean.
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [logins, setLogins] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Role-editor state
  const [editing, setEditing] = useState(null); // 'new' | role object
  const [name, setName] = useState("");
  const [selectedCodes, setSelectedCodes] = useState([]);

  // Assignment-creator state — supports multi-project selection.
  const [newAssignmentLoginId, setNewAssignmentLoginId] = useState("");
  const [newAssignmentRoleId, setNewAssignmentRoleId] = useState("");
  const [newAssignmentProjectIds, setNewAssignmentProjectIds] = useState([]);
  const [creatingAssignment, setCreatingAssignment] = useState(false);

  const developerCustomerId = userInfo?.loginIdentifierDetails?.customer?.customerId;

  const load = async () => {
    setLoading(true);
    try {
      const [permResponse, roleResponse, projectsResponse, loginResponse, assignmentResponse] =
        await Promise.all([
          fetchPermissions(),
          fetchRoles(),
          fetchProjects({ customerId: developerCustomerId }),
          axiosInstance.get("/login", {
            params: developerCustomerId ? { loginIdentifier: developerCustomerId } : {},
          }),
          fetchRoleAssignments(),
        ]);
      setPermissions(permResponse || []);
      setRoles(roleResponse || []);
      // fetchProjects may return paginated or array shape
      const projArr = Array.isArray(projectsResponse)
        ? projectsResponse
        : projectsResponse?.items || [];
      setProjects(projArr);
      const loginArr = Array.isArray(loginResponse) ? loginResponse : loginResponse?.items || [];
      setLogins(loginArr);
      setAssignments(assignmentResponse || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load RBAC data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, developerCustomerId]);

  const groupedAssignments = useMemo(() => {
    const byLogin = {};
    for (const a of assignments) {
      if (!byLogin[a.loginId]) byLogin[a.loginId] = [];
      byLogin[a.loginId].push(a);
    }
    return byLogin;
  }, [assignments]);

  // Derived: persona of the currently picked login (for the assignment form).
  // Personas the staff-and-BV split here cares about are STAFF and BV_ASSOCIATE.
  const personaOfPickedLogin = useMemo(() => {
    if (!newAssignmentLoginId) return null;
    const login = logins.find(
      (l) => String(l.loginId ?? l.id) === String(newAssignmentLoginId)
    );
    return login?.persona || login?.personaType || null;
  }, [newAssignmentLoginId, logins]);

  const isBvLoginPicked = personaOfPickedLogin === "BV_ASSOCIATE";

  // BV Associate predefined role row (used to auto-select role when BV login is picked).
  const bvAssociateRole = useMemo(
    () => roles.find((r) => r.isPredefined && r.code === "BV_ASSOCIATE") || null,
    [roles]
  );

  // Roles offered in the assignment dropdown depend on the picked login's persona.
  const assignableRoles = useMemo(() => {
    if (isBvLoginPicked) {
      return bvAssociateRole ? [bvAssociateRole] : [];
    }
    // Staff persona — every role EXCEPT the empty BV Associate predefined.
    return roles.filter((r) => !(r.isPredefined && r.code === "BV_ASSOCIATE"));
  }, [isBvLoginPicked, bvAssociateRole, roles]);

  // When a BV login is picked, force the role to BV Associate so the user
  // can't accidentally try to apply a Staff role to them. When switching
  // back to a Staff login, reset the role so they re-pick deliberately.
  useEffect(() => {
    if (isBvLoginPicked && bvAssociateRole) {
      setNewAssignmentRoleId(String(bvAssociateRole.rbacRoleId));
    } else if (personaOfPickedLogin === "STAFF") {
      // If the previously selected role was BV Associate, clear it.
      const currentRole = roles.find(
        (r) => String(r.rbacRoleId) === String(newAssignmentRoleId)
      );
      if (currentRole?.isPredefined && currentRole?.code === "BV_ASSOCIATE") {
        setNewAssignmentRoleId("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBvLoginPicked, bvAssociateRole, personaOfPickedLogin]);

  if (!allowed) return <Unauthorized />;
  if (loading) return <div className="p-6">Loading...</div>;

  const grouped = groupByPermissionGroup(permissions);

  // ---------- Role editor handlers ----------
  const openNew = () => {
    setEditing("new");
    setName("");
    setSelectedCodes([]);
  };
  const openEdit = (role) => {
    setEditing(role);
    setName(role.name);
    setSelectedCodes(role.permissions || []);
  };
  const cancel = () => {
    setEditing(null);
    setName("");
    setSelectedCodes([]);
  };
  const togglePermission = (code) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };
  const save = async () => {
    if (!name.trim()) {
      toast.error("Role name is required");
      return;
    }
    try {
      if (editing === "new") {
        await createRole({ name: name.trim(), permissionCodes: selectedCodes });
        toast.success("Role created");
      } else {
        await updateRole(editing.rbacRoleId, {
          name: name.trim(),
          permissionCodes: selectedCodes,
        });
        toast.success("Role updated");
      }
      cancel();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save role");
    }
  };
  const removeRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await deleteRole(role.rbacRoleId);
      toast.success("Role deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete role");
    }
  };

  // ---------- Assignment handlers ----------
  const handleCreateAssignment = async () => {
    if (!newAssignmentLoginId || !newAssignmentRoleId || newAssignmentProjectIds.length === 0) {
      toast.error("Pick a login, role, and at least one project");
      return;
    }
    setCreatingAssignment(true);
    try {
      const projectIds = newAssignmentProjectIds.map((id) => Number(id));
      if (projectIds.length === 1) {
        await createRoleAssignment({
          loginId: Number(newAssignmentLoginId),
          rbacRoleId: Number(newAssignmentRoleId),
          projectId: projectIds[0],
        });
        toast.success("Role assigned");
      } else {
        const created = await createRoleAssignmentsBulk({
          loginId: Number(newAssignmentLoginId),
          rbacRoleId: Number(newAssignmentRoleId),
          projectIds,
        });
        const newCount = Array.isArray(created) ? created.length : projectIds.length;
        toast.success(
          newCount === projectIds.length
            ? `Role assigned on ${newCount} project${newCount === 1 ? "" : "s"}`
            : `Assigned on ${newCount} new project${newCount === 1 ? "" : "s"} (others already had this role)`
        );
      }
      setNewAssignmentLoginId("");
      setNewAssignmentRoleId("");
      setNewAssignmentProjectIds([]);
      setAssignModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign role");
    } finally {
      setCreatingAssignment(false);
    }
  };

  const toggleAssignmentProject = (id) => {
    const key = String(id);
    setNewAssignmentProjectIds((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const removeAssignment = async (assignment) => {
    if (
      !window.confirm(
        `Remove "${assignment.rbacRoleName}" from login #${assignment.loginId} on project #${assignment.projectId}?`
      )
    )
      return;
    try {
      await deleteRoleAssignment(assignment.assignmentId);
      toast.success("Assignment removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove assignment");
    }
  };

  // Lookup helpers for display
  const projectLabel = (id) => {
    const p = projects.find((x) => x.projectId === id || x.id === id);
    return p ? p.projectName || p.project_name || `#${id}` : `#${id}`;
  };
  const roleLabel = (id) => {
    const r = roles.find((x) => x.rbacRoleId === id);
    return r ? r.name : `#${id}`;
  };

  const predefinedRolesCount = roles.filter((r) => r.isPredefined).length;
  const customRolesCount = roles.filter((r) => !r.isPredefined).length;

  return (
    <div className="space-y-5">
      {/* Header strip — dark slate gradient, matches the rest of the app's
          section headers (FormModal, Onboarding, Settings). Inline Shield
          icon removed; the page header alone carries the section identity. */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 to-slate-600 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Role Based Access</h2>
          <p className="text-xs text-slate-300 mt-0.5">
            Define what users can do, then assign roles per project.
          </p>
        </div>
        {activeTab === "roles" && (
          <IconButton
            left
            icon={<Plus size={16} className="mr-2" />}
            onClick={openNew}
          >
            New Custom Role
          </IconButton>
        )}
        {activeTab === "assignments" && (
          <IconButton
            left
            icon={<Plus size={16} className="mr-2" />}
            onClick={() => setAssignModalOpen(true)}
          >
            Assign Role
          </IconButton>
        )}
      </div>

      {/* Tabs — pill style */}
      <div className="inline-flex p-1 bg-gray-100 rounded-xl">
        {[
          { id: "roles", label: "Roles & Permissions", icon: KeyRound, count: roles.length },
          { id: "assignments", label: "Role Assignments", icon: Briefcase, count: assignments.length },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <Icon size={14} className={isActive ? "text-flamingo-500" : ""} />
              <span>{t.label}</span>
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  isActive ? "bg-flamingo-100 text-flamingo-700" : "bg-gray-200 text-gray-600"
                }`}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {activeTab === "roles" && (
        <div className="space-y-4">
          {/* Stats — flamingo + slate/grey shades only */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3.5 flex items-center gap-3 ring-1 ring-flamingo-100">
              <div className="w-10 h-10 rounded-xl bg-flamingo-500 text-white flex items-center justify-center">
                <Layers size={18} />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Roles</p>
                <p className="text-xl font-bold text-gray-900 leading-tight">{roles.length}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3.5 flex items-center gap-3 ring-1 ring-slate-100">
              <div className="w-10 h-10 rounded-xl bg-slate-700 text-white flex items-center justify-center">
                <Lock size={18} />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Predefined</p>
                <p className="text-xl font-bold text-gray-900 leading-tight">{predefinedRolesCount}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3.5 flex items-center gap-3 ring-1 ring-flamingo-100">
              <div className="w-10 h-10 rounded-xl bg-flamingo-400 text-white flex items-center justify-center">
                <Sparkles size={18} />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Custom</p>
                <p className="text-xl font-bold text-gray-900 leading-tight">{customRolesCount}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3.5 flex items-center gap-3 ring-1 ring-slate-100">
              <div className="w-10 h-10 rounded-xl bg-slate-500 text-white flex items-center justify-center">
                <Hash size={18} />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Permissions</p>
                <p className="text-xl font-bold text-gray-900 leading-tight">{permissions.length}</p>
              </div>
            </div>
          </div>

          {/* Role editor — opens as a canonical modal. Title flips between
              create and edit based on the `editing` state ('new' | role obj). */}
          <FormModal
            open={!!editing}
            onClose={cancel}
            title={editing === "new" ? "Create Custom Role" : editing ? `Edit: ${editing.name}` : ""}
            size="2xl"
          >
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                    Role name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Junior Sales (read-only)"
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Permissions
                    </label>
                    <span className="text-xs text-gray-500">
                      {selectedCodes.length} of {permissions.length} selected
                    </span>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(grouped).map(([group, perms]) => {
                      const theme = groupTheme[group] || groupTheme.CROSS_CUTTING;
                      const allChecked = perms.every((p) => selectedCodes.includes(p.code));
                      return (
                        <div
                          key={group}
                          className="rounded-xl border border-gray-200 bg-gray-50/40 overflow-hidden"
                        >
                          <div className="px-4 py-2.5 bg-white border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${theme.dot}`} />
                              <span className="text-sm font-semibold text-gray-900">
                                {theme.label}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({perms.filter((p) => selectedCodes.includes(p.code)).length}/{perms.length})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                if (allChecked) {
                                  setSelectedCodes((prev) =>
                                    prev.filter((c) => !perms.some((p) => p.code === c))
                                  );
                                } else {
                                  setSelectedCodes((prev) =>
                                    Array.from(new Set([...prev, ...perms.map((p) => p.code)]))
                                  );
                                }
                              }}
                              className="text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
                            >
                              {allChecked ? "Clear" : "Select all"}
                            </button>
                          </div>
                          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {perms.map((p) => {
                              const checked = selectedCodes.includes(p.code);
                              return (
                                <label
                                  key={p.code}
                                  className={`flex items-start gap-2.5 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${
                                    checked
                                      ? "bg-flamingo-50 border-flamingo-200"
                                      : "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => togglePermission(p.code)}
                                    className="mt-0.5 rounded text-flamingo-600 focus:ring-flamingo-300"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-mono font-medium text-gray-900 break-all">
                                      {p.code}
                                    </p>
                                    {p.description && (
                                      <p className="text-[11px] text-gray-500 mt-0.5">
                                        {p.description}
                                      </p>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-2 border-t border-gray-100">
                  <button
                    onClick={cancel}
                    className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={save}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white text-sm font-semibold shadow-sm hover:shadow-md hover:from-flamingo-600 hover:to-flamingo-700 transition-all duration-200"
                  >
                    <Save size={14} />
                    Save Role
                  </button>
                </div>
              </div>
          </FormModal>

          {/* Roles cards */}
          {roles.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-300 px-6 py-12 text-center">
              <div className="w-14 h-14 mx-auto rounded-xl bg-gray-100 flex items-center justify-center">
                <Shield size={24} className="text-gray-400" />
              </div>
              <p className="mt-3 text-sm font-semibold text-gray-700">No roles defined</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {roles.map((role) => {
                const accent = predefinedRoleAccent(role.code || "CUSTOM");
                const perms = role.permissions || [];
                return (
                  <div
                    key={role.rbacRoleId}
                    className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-flamingo-200 transition-all duration-200 overflow-hidden flex flex-col"
                  >
                    <div className={`h-1.5 bg-gradient-to-r ${accent}`} />
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 truncate">
                            {role.name}
                          </h4>
                          <div className="mt-1 flex items-center gap-1.5">
                            {role.isPredefined ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                <Lock size={9} />
                                Predefined
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-flamingo-100 text-flamingo-700 border border-flamingo-200">
                                <Sparkles size={9} />
                                Custom
                              </span>
                            )}
                          </div>
                        </div>
                        {!role.isPredefined && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => openEdit(role)}
                              className="p-1.5 rounded-lg text-gray-500 hover:text-flamingo-600 hover:bg-flamingo-50 transition-colors"
                              title="Edit"
                            >
                              <Edit3 size={14} />
                            </button>
                            <button
                              onClick={() => removeRole(role)}
                              className="p-1.5 rounded-lg text-gray-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex-1">
                        <p className="text-xs text-gray-500 mb-2">
                          {perms.length} permission{perms.length === 1 ? "" : "s"}
                        </p>
                        {perms.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">
                            No permissions — scope-only marker.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {perms.slice(0, 6).map((code) => (
                              <span
                                key={code}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono font-medium bg-flamingo-50 text-flamingo-700 border border-flamingo-100"
                              >
                                {code}
                              </span>
                            ))}
                            {perms.length > 6 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-700">
                                +{perms.length - 6}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === "assignments" && (
        <div className="space-y-4">
          {/* Assign-role flow — opens via the page-header button. Body is a
              3-step picker (user → role → projects); inline state owned by
              the parent so the modal stays a thin presentation wrapper. */}
          <FormModal
            open={assignModalOpen}
            onClose={() => setAssignModalOpen(false)}
            title="Assign a Role"
            size="lg"
          >
            <div className="space-y-4">
              {/* Step 1 — Login */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    <UserCircle2 size={12} />
                    User
                  </span>
                </label>
                <div className="relative">
                  <select
                    value={newAssignmentLoginId}
                    onChange={(e) => setNewAssignmentLoginId(e.target.value)}
                    className="w-full appearance-none pl-3 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400"
                  >
                    <option value="">Pick a user…</option>
                    {logins.map((l) => (
                      <option key={l.loginId ?? l.id} value={l.loginId ?? l.id}>
                        {l.username} ({l.name || "—"}) — {l.persona || "STAFF"}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>

              {/* Step 2 — Role */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck size={12} />
                    Role
                    {isBvLoginPicked && (
                      <span className="text-[10px] text-gray-400 normal-case font-normal">
                        — locked for BV users
                      </span>
                    )}
                  </span>
                </label>
                <div className="relative">
                  <select
                    value={newAssignmentRoleId}
                    onChange={(e) => setNewAssignmentRoleId(e.target.value)}
                    disabled={isBvLoginPicked || !newAssignmentLoginId}
                    className="w-full appearance-none pl-3 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    {!newAssignmentLoginId && <option value="">Pick a user first…</option>}
                    {newAssignmentLoginId && !isBvLoginPicked && (
                      <option value="">Pick a role…</option>
                    )}
                    {assignableRoles.map((r) => (
                      <option key={r.rbacRoleId} value={r.rbacRoleId}>
                        {r.name} {r.isPredefined ? "(predefined)" : "(custom)"}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>

              {/* Step 3 — Projects: searchable multi-select dropdown.
                  newAssignmentProjectIds is kept as string IDs (matching the
                  prior checkbox-list shape); we convert to option objects on
                  display and back on change. */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase size={12} />
                    Projects
                    <span className="text-[10px] text-gray-400 normal-case font-normal">
                      — {newAssignmentProjectIds.length} selected
                    </span>
                  </span>
                </label>
                {(() => {
                  const projectOptions = projects.map((p) => {
                    const pid = p.projectId ?? p.id;
                    return {
                      value: String(pid),
                      label: p.projectName || p.project_name || `Project #${pid}`,
                    };
                  });
                  const selectedOptions = projectOptions.filter((o) =>
                    newAssignmentProjectIds.includes(o.value),
                  );
                  return (
                    <CustomSelect
                      isMulti
                      isSearchable
                      placeholder={
                        projects.length === 0
                          ? "No projects available"
                          : "Pick one or more projects…"
                      }
                      options={projectOptions}
                      value={selectedOptions}
                      onChange={(opts) =>
                        setNewAssignmentProjectIds((opts || []).map((o) => o.value))
                      }
                      isDisabled={projects.length === 0}
                      styles={{}}
                      classNamePrefix="select"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      size="form"
                    />
                  );
                })()}
              </div>

              <div className="flex justify-end pt-2 border-t border-gray-100">
                <button
                  onClick={handleCreateAssignment}
                  disabled={
                    creatingAssignment ||
                    !newAssignmentLoginId ||
                    !newAssignmentRoleId ||
                    newAssignmentProjectIds.length === 0
                  }
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white text-sm font-semibold shadow-sm hover:shadow-md hover:from-flamingo-600 hover:to-flamingo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400"
                >
                  {creatingAssignment ? "Assigning…" : "Assign Role"}
                  {!creatingAssignment && <ChevronRight size={14} />}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                A user can hold multiple roles across different projects. Re-assigning the same
                (user, role, project) tuple is a no-op.
              </p>
            </div>
          </FormModal>

          {/* Existing assignments */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Existing Assignments</h3>
              <span className="text-xs text-gray-500">
                {assignments.length} total · {Object.keys(groupedAssignments).length} user
                {Object.keys(groupedAssignments).length === 1 ? "" : "s"}
              </span>
            </div>

            {assignments.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-300 px-6 py-12 text-center">
                <div className="w-14 h-14 mx-auto rounded-xl bg-gray-100 flex items-center justify-center">
                  <Briefcase size={24} className="text-gray-400" />
                </div>
                <p className="mt-3 text-sm font-semibold text-gray-700">No role assignments yet</p>
                <p className="text-xs text-gray-500 mt-1">
                  Pick a user above and assign them a role on one or more projects.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupedAssignments).map(([loginId, assigns]) => {
                  const login = logins.find((l) => (l.loginId ?? l.id) === Number(loginId));
                  return (
                    <div
                      key={loginId}
                      className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
                    >
                      <div className="px-5 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-flamingo-400 to-flamingo-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
                            {(login?.name || login?.username || "?")
                              .trim()
                              .split(/\s+/)
                              .slice(0, 2)
                              .map((p) => p[0]?.toUpperCase())
                              .join("") || "?"}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {login?.name || login?.username || `Login #${loginId}`}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              @{login?.username || "—"} ·{" "}
                              <span className="inline-flex items-center gap-1">
                                <ShieldCheck size={10} />
                                {(login?.persona || "STAFF").replace(/_/g, " ")}
                              </span>
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-flamingo-600 shrink-0">
                          {assigns.length} assignment{assigns.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {assigns.map((a) => (
                          <div
                            key={a.assignmentId}
                            className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-flamingo-50 text-flamingo-700 border border-flamingo-200">
                                <Shield size={11} />
                                {a.rbacRoleName || roleLabel(a.rbacRoleId)}
                              </span>
                              <ChevronRight size={12} className="text-gray-300" />
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200 truncate">
                                <Briefcase size={11} />
                                {projectLabel(a.projectId)}
                              </span>
                            </div>
                            <button
                              onClick={() => removeAssignment(a)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                              title="Remove"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageRolesPage;
